import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import {
  getAttributeSpecifications,
  createAttributeSpecification,
  updateAttributeSpecification,
  deleteAttributeSpecification,
} from '../../../app/api';
import { normalizeAttributeSpec } from '../public/categoryUtils';
import { useTranslation } from '../../../app/LanguageContext';

export default function AttributeSpecificationsManageDialog({ open, onClose, onChanged }) {
  const { translate } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState({ name: '', type: 'text', values: [] });
  const [editingId, setEditingId] = useState('');
  const [valueInput, setValueInput] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await getAttributeSpecifications({ limit: 200 });
      const list = (res.data?.attributes ?? res.data ?? []).map(normalizeAttributeSpec).filter(Boolean);
      setItems(list);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) void load();
  }, [open]);

  const resetDraft = () => {
    setDraft({ name: '', type: 'text', values: [] });
    setEditingId('');
    setValueInput('');
  };

  const handleSave = async () => {
    const name = draft.name.trim();
    if (!name) return;
    const payload = {
      name,
      type: draft.type,
      values: (draft.values || []).map((v, i) => ({ value: v, display_order: i })),
    };
    if (editingId) {
      await updateAttributeSpecification(editingId, payload);
    } else {
      await createAttributeSpecification(payload);
    }
    resetDraft();
    await load();
    onChanged?.();
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setDraft({
      name: item.name,
      type: item.type,
      values: (item.values || []).map((v) => v.value),
    });
  };

  const handleDelete = async (id) => {
    await deleteAttributeSpecification(id);
    if (editingId === id) resetDraft();
    await load();
    onChanged?.();
  };

  const addValue = () => {
    const v = valueInput.trim();
    if (!v || draft.values.includes(v)) return;
    setDraft((prev) => ({ ...prev, values: [...prev.values, v] }));
    setValueInput('');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{translate('categories.attributes_manage_title')}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mb: 2 }}>
          <TextField
            label={translate('categories.attribute_name')}
            value={draft.name}
            onChange={(e) => setDraft((p) => ({ ...p, name: e.target.value }))}
            fullWidth
            size="small"
          />
          <FormControl fullWidth size="small">
            <InputLabel>{translate('categories.attribute_type')}</InputLabel>
            <Select
              label={translate('categories.attribute_type')}
              value={draft.type}
              onChange={(e) => setDraft((p) => ({ ...p, type: e.target.value, values: [] }))}
            >
              <MenuItem value="text">{translate('categories.attribute_type_text')}</MenuItem>
              <MenuItem value="select">{translate('categories.attribute_type_select')}</MenuItem>
              <MenuItem value="checkbox">{translate('categories.attribute_type_checkbox')}</MenuItem>
            </Select>
          </FormControl>
          {(draft.type === 'select' || draft.type === 'checkbox') && (
            <Box>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  label={translate('categories.attribute_value')}
                  value={valueInput}
                  onChange={(e) => setValueInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addValue())}
                />
                <Button variant="outlined" onClick={addValue}>{translate('common.add')}</Button>
              </Stack>
              <Stack direction="row" flexWrap="wrap" gap={0.5}>
                {draft.values.map((v) => (
                  <Chip
                    key={v}
                    label={v}
                    size="small"
                    onDelete={() => setDraft((p) => ({ ...p, values: p.values.filter((x) => x !== v) }))}
                  />
                ))}
              </Stack>
            </Box>
          )}
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleSave}>
            {editingId ? translate('common.save') : translate('common.create')}
          </Button>
        </Stack>
        <List dense>
          {items.map((item) => (
            <ListItem
              key={item.id}
              secondaryAction={
                <Box>
                  <IconButton size="small" onClick={() => handleEdit(item)}>
                    <SettingsOutlinedIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(item.id)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              }
            >
              <ListItemText
                primary={item.name}
                secondary={`${item.type}${item.values?.length ? ` · ${item.values.map((v) => v.value).join(', ')}` : ''}`}
              />
            </ListItem>
          ))}
          {!loading && !items.length && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
              {translate('categories.attributes_empty')}
            </Typography>
          )}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{translate('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
